import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Button,
  Checkbox,
  EmptyState,
  Input,
  Modal,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useMemo, useState } from 'react';

import {
  useGenerateVariantsMutation,
  useOptionsQuery,
  type GenerateCombinationInput,
} from '../api';
import type { ProductOptionValue, ProductOptionWithValues } from '../types';

import { useToast } from '@/components/ui/toast/ToastProvider';

interface GeneratedRow {
  readonly key: string;
  readonly optionValueIds: readonly string[];
  readonly label: string;
  readonly sku: string;
  readonly priceOverride: string;
  readonly included: boolean;
}

/** Every axis carries at least one selected value — callers filter empty axes out before calling this. */
function cartesianProduct(
  axes: readonly (readonly ProductOptionValue[])[],
): ProductOptionValue[][] {
  return axes.reduce<ProductOptionValue[][]>(
    (acc, axisValues) => acc.flatMap((combo) => axisValues.map((value) => [...combo, value])),
    [[]],
  );
}

function skuFragment(value: string): string {
  return value
    .toUpperCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Variant-improvements lot — the generator's own request (item 2): the
 * combination preview with exclusion checkboxes is computed ENTIRELY
 * client-side (the browser already has every option/value from
 * `useOptionsQuery`); one server call at the confirm step, naming every
 * combination NOT excluded, created in one all-or-nothing transaction
 * (`ProductOptionRepository.generateVariants()`'s own guarantee).
 */
export function VariantGeneratorWizard({
  productId,
  productSku,
}: {
  productId: string;
  productSku: string;
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const optionsQuery = useOptionsQuery(productId);
  const generateMutation = useGenerateVariantsMutation(productId);

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<0 | 1>(0);
  const [selectedByOption, setSelectedByOption] = useState<Record<string, Set<string>>>({});
  const [rows, setRows] = useState<GeneratedRow[]>([]);

  const options = optionsQuery.data ?? [];

  function toggleValue(option: ProductOptionWithValues, valueId: string): void {
    setSelectedByOption((previous) => {
      const current = new Set(previous[option.id] ?? []);
      if (current.has(valueId)) current.delete(valueId);
      else current.add(valueId);
      return { ...previous, [option.id]: current };
    });
  }

  const axes = useMemo(
    () =>
      options
        .map((option) => ({
          option,
          values: option.values.filter(
            (value) => selectedByOption[option.id]?.has(value.id) === true,
          ),
        }))
        .filter((axis) => axis.values.length > 0),
    [options, selectedByOption],
  );

  function goToPreview(): void {
    const combinations = cartesianProduct(axes.map((axis) => axis.values));
    setRows(
      combinations.map((combo) => ({
        key: combo.map((v) => v.id).join(','),
        optionValueIds: combo.map((v) => v.id),
        label: combo.map((v) => v.value).join(' / '),
        sku: `${skuFragment(productSku)}-${combo.map((v) => skuFragment(v.value)).join('-')}`,
        priceOverride: '',
        included: true,
      })),
    );
    setStep(1);
  }

  function close(): void {
    setOpen(false);
    setStep(0);
    setSelectedByOption({});
    setRows([]);
  }

  async function onGenerate(): Promise<void> {
    const combinations: GenerateCombinationInput[] = rows
      .filter((row) => row.included)
      .map((row) => ({
        optionValueIds: row.optionValueIds,
        sku: row.sku.trim(),
        priceOverride:
          row.priceOverride.trim() === '' ? null : Number.parseFloat(row.priceOverride),
      }));
    if (combinations.length === 0) return;
    try {
      const created = await generateMutation.mutateAsync(combinations);
      toast.success(t('merchant.products.generator.generatedToast', { count: created.length }));
      close();
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  const includedCount = rows.filter((row) => row.included).length;

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('merchant.products.generator.openButton')}
      </Button>

      <Modal
        open={open}
        title={t('merchant.products.generator.title')}
        onClose={close}
        footer={
          step === 0 ? (
            <Button onClick={goToPreview} disabled={axes.length === 0}>
              {t('merchant.products.generator.next')}
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setStep(0);
                }}
              >
                {t('merchant.products.generator.back')}
              </Button>
              <Button
                loading={generateMutation.isPending}
                disabled={includedCount === 0}
                onClick={() => {
                  void onGenerate();
                }}
              >
                {t('merchant.products.generator.generate', { count: includedCount })}
              </Button>
            </>
          )
        }
      >
        <Stepper
          steps={[
            { id: 'select', label: t('merchant.products.generator.stepSelect') },
            { id: 'preview', label: t('merchant.products.generator.stepPreview') },
          ]}
          activeIndex={step}
        />

        {options.length === 0 ? (
          <EmptyState title={t('merchant.products.generator.noOptions')} />
        ) : step === 0 ? (
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-sm text-inkFaint">{t('merchant.products.generator.selectValues')}</p>
            {options.map((option) => (
              <div key={option.id}>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-inkFaint">
                  {option.name}
                </p>
                <div className="flex flex-wrap gap-3">
                  {option.values.map((value) => (
                    <Checkbox
                      key={value.id}
                      label={value.value}
                      checked={selectedByOption[option.id]?.has(value.id) === true}
                      onChange={() => {
                        toggleValue(option, value.id);
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            <p className="text-sm text-inkFaint">
              {t('merchant.products.generator.duplicateSkuHint')}
            </p>
            <Table caption={t('merchant.products.generator.previewTitle')}>
              <TableHead>
                <TableRow>
                  {/* One column for « include » + combination: the checkbox's own label IS the combination, and a
                      second copy of it squeezed the SKU field down to two visible characters in this 32rem modal. */}
                  <TableHeaderCell>
                    {t('merchant.products.generator.colCombination')}
                  </TableHeaderCell>
                  <TableHeaderCell>{t('merchant.products.generator.colSku')}</TableHeaderCell>
                  <TableHeaderCell align="end">
                    {t('merchant.products.generator.colPrice')}
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.key}>
                    <TableCell>
                      <Checkbox
                        label={row.label}
                        checked={row.included}
                        onChange={(checked) => {
                          setRows((previous) =>
                            previous.map((r, i) => (i === index ? { ...r, included: checked } : r)),
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        aria-label={`${t('merchant.products.generator.colSku')} — ${row.label}`}
                        className="min-w-[13rem] font-mono text-xs"
                        value={row.sku}
                        disabled={!row.included}
                        onChange={(event) => {
                          const sku = event.currentTarget.value;
                          setRows((previous) =>
                            previous.map((r, i) => (i === index ? { ...r, sku } : r)),
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell align="end">
                      <Input
                        type="number"
                        step="0.001"
                        min="0"
                        className="w-24"
                        disabled={!row.included}
                        value={row.priceOverride}
                        onChange={(event) => {
                          const priceOverride = event.currentTarget.value;
                          setRows((previous) =>
                            previous.map((r, i) => (i === index ? { ...r, priceOverride } : r)),
                          );
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Modal>
    </>
  );
}
